import { Instagram, ArrowUpRight } from "lucide-react";

/**
 * Public bridge between Instagram and Smartasaurus.
 * Rendered on every Discovery that was seeded from a viral reel.
 * Message: Instagram is the teaser. Smartasaurus is where the complete story lives.
 */
export function InstagramBridge({
  permalink,
  views,
}: {
  permalink: string;
  views: number | null;
}) {
  const viewCopy = formatViews(views);
  return (
    <aside
      className="my-6 rounded-2xl border border-pink-500/30 bg-gradient-to-br from-pink-500/10 via-purple-500/5 to-orange-500/10 p-4 sm:p-5"
      aria-label="From Instagram"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 to-orange-500 text-white">
          <Instagram className="h-5 w-5" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-pink-500">From Instagram</p>
          <p className="mt-1 text-sm text-foreground/90 leading-relaxed">
            {viewCopy ? (
              <>
                <span className="font-semibold">{viewCopy}</span> saw this on our Instagram.
                You're now reading the complete story.
              </>
            ) : (
              <>This Discovery was inspired by one of our most popular Instagram reels. You're reading the complete story.</>
            )}
          </p>
          <a
            href={permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-pink-600 hover:text-pink-700 dark:text-pink-400 dark:hover:text-pink-300"
          >
            Watch the original reel
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          </a>
        </div>
      </div>
    </aside>
  );
}

function formatViews(n: number | null): string | null {
  if (!n || n < 1000) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M people`;
  if (n >= 1_000) return `${Math.round(n / 1000)}K people`;
  return `${n} people`;
}
